import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal, { ModalContent, ModalTitle } from '../../../../ui/Layout/Modals/Modal/Modal';
import SongForm, { SongFormProps } from '../../components/SongForm/SongForm';
import { getSpotifySong, patchSong, refreshSongFromSpotify, SongSimple, uploadSongImage } from '@round/api';
import useDownloadImage from '../../../../Hooks/useDownloadImage';
import { showNotification } from '../../../../helpers';
import useAbortableEffect from '../../../../Hooks/useAbortableEffect';
import { GenericDropdownOption } from '../../../../App.types';
import { Banner } from '@round/ui-kit';
import styles from './UpdateSongModal.module.css';
import { mapApiErrorsToFormikErrors } from '../../../../utility/utility';
import Button from '../../../../ui/Buttons/Button/Button';
import SongFromSpotifyForm, {
    SongFromSpotifyFormProps,
} from '../../components/SongFromSpotifyForm/SongFromSpotifyForm';

type UpdateSongModalProps = {
    isOpen: boolean;
    onClose: () => void;
    song: SongSimple | null;
    onUpdate: (song: SongSimple) => void;
    onRefreshed?: (song: SongSimple) => void;
};

const UpdateSongModal = ({ onClose, isOpen, song, onUpdate, onRefreshed }: UpdateSongModalProps) => {
    const { image } = useDownloadImage(song?.image);
    const [spotifySong, setSpotifySong] = useState<GenericDropdownOption<number> | null>(null);
    const [errorLoadingSpotifySong, setErrorLoadingSpotifySong] = useState(false);
    const [isRefreshingFromSpotify, setIsRefreshingFromSpotify] = useState(false);
    const [isLinkToSpotifySongFormActive, setIsLinkToSpotifySongFormActive] = useState(false);

    useAbortableEffect(
        (signal) => {
            if (typeof song?.spotify_song !== 'number') {
                return;
            }

            setErrorLoadingSpotifySong(false);
            getSpotifySong(song?.spotify_song, { signal })
                .then((response) => {
                    if (response.status === 200) {
                        setSpotifySong({ value: response.data.id, label: response.data.title });
                    }
                })
                .catch(() => setErrorLoadingSpotifySong(true));
        },
        [song?.spotify_song]
    );

    useEffect(() => {
        if (!isOpen) {
            setSpotifySong(null);
        }
    }, [isOpen]);

    const manualFormInitialValues: SongFormProps['initialValues'] = useMemo(
        () => ({
            title: song?.title ?? '',
            artists: song?.artists.map((a) => ({ value: a.id, label: a.name })) ?? [],
            image: image,
            soundcloud_url: song?.soundcloud_url ?? '',
            spotify_song: spotifySong,
        }),
        [image, song?.artists, song?.soundcloud_url, song?.title, spotifySong]
    );

    const spotifyFormInitialValues: SongFromSpotifyFormProps['initialValues'] = useMemo(
        () => ({
            url: '',
            song: song ? { value: song.id, label: song.title } : null,
        }),
        [song]
    );

    const handleSubmit: SongFormProps['handleSubmit'] = useCallback(
        async ({ image, ...values }, helpers) => {
            if (!song) {
                return;
            }

            let updatedSong = song;
            try {
                helpers.setSubmitting(true);
                const response = await patchSong(song.id, {
                    title: values.title,
                    soundcloud_url: values.soundcloud_url,
                    spotify_song: values?.spotify_song?.value ?? null,
                    artist_ids: values?.artists.map((a) => a.value),
                });

                if (response.status === 404) {
                    showNotification('Could not find a song', 'error');
                    return;
                }

                if (response.status === 400) {
                    helpers.setErrors(mapApiErrorsToFormikErrors(response.data));
                    return;
                }

                updatedSong = response.data;
                if (image) {
                    updatedSong = await uploadSongImage(song.id, image);
                }

                onUpdate(updatedSong);
                showNotification('Updated', 'info');
            } catch {
                showNotification('Could not update', 'error');
            } finally {
                helpers.setSubmitting(false);
            }
        },
        [onUpdate, song]
    );

    const handleRefreshFromSpotify = useCallback(async () => {
        if (!song || typeof song.spotify_song !== 'number') {
            return;
        }

        try {
            setIsRefreshingFromSpotify(true);
            const refreshSongResponse = await refreshSongFromSpotify(song?.id);
            if (refreshSongResponse.status === 404) {
                showNotification('Song not found', 'error');
                return;
            }

            if (refreshSongResponse.status === 400) {
                showNotification(refreshSongResponse.data.error, 'error');
                return;
            }

            if (typeof onRefreshed === 'function') {
                onRefreshed(refreshSongResponse.data);
            }

            showNotification('Song data refreshed', 'info');
        } catch {
            showNotification('Unknown error occurred', 'error');
        } finally {
            setIsRefreshingFromSpotify(false);
        }
    }, [onRefreshed, song]);

    const handleLinkToSpotifySong: SongFromSpotifyFormProps['handleSubmit'] = useCallback(
        async (values, { setSubmitting }) => {
            if (!song || typeof song.spotify_song === 'number') {
                return;
            }

            try {
                setSubmitting(true);
                const response = await refreshSongFromSpotify(song.id, values.url);
                if (response.status === 404) {
                    showNotification('Song not found', 'error');
                    return;
                }

                if (response.status === 400) {
                    showNotification(response.data.error, 'error');
                    return;
                }

                if (typeof onRefreshed === 'function') {
                    onRefreshed(response.data);
                }

                setIsLinkToSpotifySongFormActive(false);
                showNotification('Song linked', 'info');
            } catch {
                showNotification('Could not link song', 'error');
            } finally {
                setSubmitting(false);
            }
        },
        [onRefreshed, song]
    );

    return (
        <Modal closeOnOverlayClick isOpen={isOpen} onClose={onClose}>
            <ModalTitle>{isLinkToSpotifySongFormActive ? 'Link to spotify song' : 'Update Song'}</ModalTitle>
            <ModalContent>
                {errorLoadingSpotifySong && (
                    <Banner appearance="warning" className={styles.banner}>
                        a problem occurred while initializing the form
                    </Banner>
                )}

                {!isLinkToSpotifySongFormActive && (
                    <SongForm
                        initialValues={manualFormInitialValues}
                        handleSubmit={handleSubmit}
                        renderActions={({ isSubmitting, submitForm }) => (
                            <div className={styles.actions}>
                                <Button type="filled" color="black" loading={isSubmitting} onClick={submitForm}>
                                    Save
                                </Button>

                                {typeof song?.spotify_song === 'number' && (
                                    <Button
                                        type="bordered"
                                        onClick={handleRefreshFromSpotify}
                                        loading={isRefreshingFromSpotify}
                                    >
                                        Refresh from spotify
                                    </Button>
                                )}

                                {typeof song?.spotify_song !== 'number' && (
                                    <Button type="link" onClick={() => setIsLinkToSpotifySongFormActive(true)}>
                                        Link to spotify song
                                    </Button>
                                )}
                            </div>
                        )}
                    />
                )}

                {isLinkToSpotifySongFormActive && (
                    <SongFromSpotifyForm
                        disabledFields={['song']}
                        initialValues={spotifyFormInitialValues}
                        handleSubmit={handleLinkToSpotifySong}
                        renderActions={({ isSubmitting, submitForm, isValid }) => (
                            <div className={styles.actions}>
                                <Button
                                    type="filled"
                                    color="black"
                                    loading={isSubmitting}
                                    disabled={!isValid}
                                    onClick={submitForm}
                                >
                                    Submit
                                </Button>

                                <Button type="link" onClick={() => setIsLinkToSpotifySongFormActive(false)}>
                                    Edit manually
                                </Button>
                            </div>
                        )}
                    />
                )}
            </ModalContent>
        </Modal>
    );
};

export default UpdateSongModal;
